// Voucher code presentation token. Always uppercase, monospace, tabular-nums,
// wrapped in a subtle gradient pill so it reads as a visual token rather than
// body text. Optional copy-to-clipboard with an aria live announcement.

import { useState } from 'react';

export interface VoucherCodeChipProps {
  code: string;
  size?: 'sm' | 'md' | 'lg';
  copyable?: boolean;
  className?: string;
}

const SIZE_CLASSES: Record<NonNullable<VoucherCodeChipProps['size']>, string> = {
  sm: 'px-2.5 py-1 text-xs',
  md: 'px-3 py-1.5 text-sm',
  lg: 'px-4 py-2 text-base',
};

export function VoucherCodeChip({ code, size = 'sm', copyable = false, className = '' }: VoucherCodeChipProps) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard API unavailable — noop */
    }
  }

  const chip = (
    <span
      className={`inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-brand-50 to-indigo-50 dark:from-brand-900/30 dark:to-indigo-900/30 ring-1 ring-brand-100 dark:ring-brand-800/50 font-mono font-bold tracking-wider text-brand-700 dark:text-brand-200 tabular-nums ${SIZE_CLASSES[size]} ${className}`}
    >
      {code}
      {copyable ? (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void handleCopy();
          }}
          aria-label={copied ? 'Copied' : 'Copy voucher code'}
          className="ml-1 rounded p-0.5 text-brand-500 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-100 transition-colors"
        >
          {copied ? (
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          )}
        </button>
      ) : null}
    </span>
  );

  // Wrap in a live region so screen readers announce "Copied" on click.
  return (
    <span className="inline-flex items-center gap-1.5">
      {chip}
      <span role="status" aria-live="polite" className="sr-only">
        {copied ? `Voucher code ${code} copied to clipboard` : ''}
      </span>
    </span>
  );
}
